import * as React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { Card } from '../../components/Card/Card'
import { Layout } from '../../components/Layout/Layout'
import Loader from '../../components/Loader/Loader'
import { Navbar } from '../../components/Navbar/Navbar'
import { CompanyId, Role, UserCompanyDto, UserProfileDto } from '../../services/api.types'
import { getFullName } from '../helpers/translations'
import { MAPPED_ROUTES, ROUTES } from './routePaths'
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop'
import GAListener from './GAListener'
import YumChallengeTabsWrapper from '../../components/YumChallengeTabs/YumChallengeTabsWrapper'
import YumChallengeTitleWrapper from '../../components/YumChallengeTitle/YumChallengeTitleWrapper'
import { t } from 'ttag'
import { hasFeatureFlag } from '../helpers/featureFlags'
import { ReactComponent as InboxIcon } from '../../assets/v3/icons/menu-inbox-inactive.svg'
import { ReactComponent as InboxActiveIcon } from '../../assets/v3/icons/menu-inbox-active.svg'

export interface IStateProps {
  hasAnyRole(roles: Role[]): boolean
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
  authenticated: boolean
  userDto: UserProfileDto
  currentRoute: Partial<Location>
  userCompanies: UserCompanyDto[]
  isSuperAdmin: boolean
  superAdminTargetCompanyName: string | null
  chatUnreadCount: number
}

export interface IDispatchProps {
  doLogOut(): void
  getWhoAmI(): void
  clearSuperAdminTargetCompany(): void
}

type IRoutesProps = IStateProps & IDispatchProps

export class Routes extends React.Component<IRoutesProps> {
  defaultUserDto = {
    //companyName: '',
    email: '',
    firstName: '',
    lastName: '',
    //role: Role.ANONYMOUS,
  }

  async componentDidMount() {
    //console.log('Routes | componentDidMount -> getWhoAmI')
    // TODO: Maybe do it only if has session?
    //const { value: session } = await Preferences.get({ key: 'session' })
    //if (session && session != 'undefined') {
    this.props.getWhoAmI()
    //}
  }

  Loading = () => (
    <Card className="pt-12">
      <Loader />
    </Card>
  )

  Fallback = () => <Redirect to={ROUTES.LANDING.to} />

  Routes = () =>
    MAPPED_ROUTES.map(({ to, component, roles }, index) => {
      const { hasAnyRole } = this.props
      if (to === ROUTES.ACTIVITIES.to && this.isActivitiesHidden()) {
        return null
      }
      // The chat list and open-conversation routes render the same component from a single
      // Route, so the Chat view stays MOUNTED when navigating between them — as separate
      // Routes the Switch would remount it, refetching the channel list and losing its state.
      if (to === ROUTES.CHAT.to) {
        return (
          hasAnyRole(roles) && (
            <Route
              exact={true}
              key={`route-${index}`}
              path={[ROUTES.CHAT.to, ROUTES.CHAT_CHANNEL.to]}
              component={component}
            />
          )
        )
      }
      if (to === ROUTES.CHAT_CHANNEL.to) {
        return null
      }
      return hasAnyRole(roles) && <Route exact={true} key={`route-${index}`} path={to} component={component} />
    })

  isStatisticsHidden = () => {
    return (
      this.props.userCompanies &&
      !this.props.userCompanies.some((uc) => {
        const showFunFacts = !uc.hideStatisticsView
        const showAdminReport = uc.reportRequired && this.props.hasRoleInCompany(uc.companyId, Role.ADMIN)
        const showUserReport = uc.reportRequired && uc.reportRequiredForUsers
        return showFunFacts || showAdminReport || showUserReport
      })
    )
  }

  // isRankingHidden = () => this.props.userCompanies && !this.props.userCompanies.some((uc) => !uc.hideRankingView)

  isProgressHidden = () => !this.props.hasAnyRole([Role.USER])

  // This app version gates the (reworked) Activities view on the beta flag; already-shipped
  // versions still check the original viewActivities flag, so admins can roll them out separately.
  isActivitiesHidden = () => !hasFeatureFlag(this.props.userCompanies, 'viewActivitiesBeta')

  Header = () => {
    const { hasAnyRole, authenticated, userDto, currentRoute } = this.props
    const { firstName, lastName, email, onboardingStatus, serviceProvider, unreadNotificationsCount } =
      userDto || this.defaultUserDto

    const currentRouteIsPublic: boolean = MAPPED_ROUTES.some((route: any) => {
      return route.isPublic && route.to === currentRoute.pathname
    })
    const isLanguageSelectionVisible = !authenticated && currentRouteIsPublic
    const isAdmin = hasAnyRole([Role.ADMIN])
    const groupChatEnabled = hasFeatureFlag(this.props.userCompanies, 'groupChat')
    const navRoutes: any = MAPPED_ROUTES.map((i) => {
      const { roles, inNavigation, to } = i

      if (to === '/statistics' && this.isStatisticsHidden()) {
        return null
      }

      /*if (to === '/ranking' && this.isRankingHidden()) {
        return null
      }*/

      if (to === '/progress' && this.isProgressHidden()) {
        return null
      }

      if (to === ROUTES.ACTIVITIES.to && this.isActivitiesHidden()) {
        return null
      }

      //const hasNavRoutes = hasAnyRole([Role.ADMIN, Role.USER]) && currentRoute.pathname !== ROUTES.REGISTER_USER.to
      const hasNavRoutes = hasAnyRole([Role.AUTHENTICATED]) && currentRoute.pathname !== ROUTES.REGISTER_USER.to
      const noSettingsForAdmin = !(isAdmin && to === ROUTES.SETTINGS.to)
      if (hasNavRoutes && hasAnyRole(roles) && inNavigation && noSettingsForAdmin && !currentRouteIsPublic) {
        // Without the group chat feature, the nav keeps the classic Inbox entry (notifications
        // feed) in place of the Chat view.
        if (to === ROUTES.CHAT.to && !groupChatEnabled) {
          return {
            ...ROUTES.NOTIFICATIONS,
            title: t`Inbox`,
            shortTitle: t`Inbox`,
            icon: InboxIcon,
            iconActive: InboxActiveIcon,
            inNavigation: true,
          }
        }
        return i
      }

      return null
    }).filter(Boolean)

    const brandCompany = this.props.userCompanies ? this.props.userCompanies.find((uc) => uc.brandLogoUrl) : false
    const brandLogoUrl = brandCompany ? brandCompany.brandLogoUrl : ''

    return (
      <Navbar
        routes={navRoutes}
        authenticated={authenticated}
        fullName={getFullName(firstName, lastName, email)}
        isLanguageSelectionVisible={isLanguageSelectionVisible}
        currentRoute={currentRoute}
        isAdmin={isAdmin}
        isSuperAdmin={this.props.isSuperAdmin}
        onboardingStatus={onboardingStatus}
        brandLogoUrl={brandLogoUrl}
        serviceProvider={serviceProvider}
        unreadNotificationsCount={unreadNotificationsCount}
        chatUnreadCount={this.props.chatUnreadCount}
        groupChatEnabled={groupChatEnabled}
      />
    )
  }

  render() {
    const { currentRoute } = this.props
    const { pathname } = currentRoute

    const isNarrow = pathname === ROUTES.SETTINGS.to

    const pathParts = currentRoute.pathname ? currentRoute.pathname.split('/') : []
    const isChallengePage =
      !!(pathParts[1] === 'challenges' && pathParts[3] && pathParts[3] != 'edit') || pathParts[1] === 'leaderboard'
    const goalId = isChallengePage ? pathParts[2] : undefined
    const isLeaderboard = pathParts[1] === 'leaderboard'

    // Because we're not using hashHistory for Cordova anymore.
    const Router = 'div'
    //const Router = isNative() ? HashRouter : 'div'
    const content = (
      <GAListener trackingId="UA-144824224-1">
        <Router className={'router'}>
          <ScrollToTop>
            <React.Suspense fallback={this.Loading()}>
              <Switch>
                {this.Routes()}
                <Route component={this.Fallback} />
              </Switch>
            </React.Suspense>
          </ScrollToTop>
        </Router>
      </GAListener>
    )

    // Don't display landing page in Cordova, tied to routePaths.ts
    const { superAdminTargetCompanyName, clearSuperAdminTargetCompany } = this.props

    return (
      <Layout header={!isLeaderboard && this.Header()} isNarrow={isNarrow}>
        {superAdminTargetCompanyName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 20px',
              backgroundColor: '#fff3cd',
              borderRadius: 8,
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#856404',
              margin: '0 0 8px 0',
            }}
          >
            <span>
              {t`Managing:`} <strong>{superAdminTargetCompanyName}</strong>
            </span>
            <button
              onClick={clearSuperAdminTargetCompany}
              style={{
                padding: '4px 12px',
                fontSize: '0.8125rem',
                cursor: 'pointer',
                border: '1px solid #856404',
                borderRadius: 6,
                backgroundColor: 'transparent',
                color: '#856404',
              }}
            >
              {t`Exit`}
            </button>
          </div>
        )}
        {isChallengePage && (
          <>
            <div className={!isLeaderboard ? 'show-on-desktop' : ''} style={{ paddingBottom: 20 }}>
              <YumChallengeTitleWrapper goalId={goalId} noMargin={true} />
            </div>
            {!isLeaderboard && <YumChallengeTabsWrapper goalId={goalId} tab={pathParts[3]} />}
          </>
        )}
        {content}
      </Layout>
    )
  }
}
